/**
 * Reports the local run to the server: positions (PlayerNetState) STATE_SEND_HZ times a
 * second and right away when the run jumps (death, respawn, next level), plus the race
 * events (death, finished level, pickup, boost) the moment they happen.
 *
 * Only racers have a NetSync; it stays silent before the start (the server ignores the
 * countdown), after the local race is finished and once the time of a race against the clock
 * is up (stop()). Every `tm` is wall-clock race time —
 * the synchronized server clock minus startAt — at the simulation tick it describes, so the
 * finish time is exact to the tick even if the frame came late.
 */
import { type PlayerNetState, type RaceEvent, SPEEDS, STATE_SEND_HZ } from '@cube-rush/shared';
import type { GameNetBridge } from './contract';
import type { LocalRun, RunEvent } from './localRun';
import { wrapAngle } from './pose';
import { raceTimeAt, wireTime } from './raceClock';

export const STATE_INTERVAL_MS = 1000 / STATE_SEND_HZ;

/** Coordinates go over the wire with millimetre-ish precision (shorter JSON). */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** The race event for a simulation event, or null if others do not need to know. */
export function raceEventFor(e: RunEvent, tm: number): RaceEvent | null {
  const event = e.event;
  switch (event.type) {
    case 'death':
      return { k: 'death', lvl: e.level, x: round3(event.x), tm };
    case 'levelComplete':
      return { k: 'level', lvl: event.level, att: event.attempts, tm };
    case 'pickup':
      return event.ignored ? null : { k: 'pickup', lvl: e.level, boost: event.boost, tm };
    case 'boostStart':
      return { k: 'boost', lvl: e.level, boost: event.boost, tm };
    default:
      return null;
  }
}

/** The local player's position as other racers see it. */
export function netStateOf(run: LocalRun, tm: number): PlayerNetState {
  const sim = run.sim;
  const s = sim.state;
  return {
    lvl: sim.levelIndex,
    x: round3(s.x + SPEEDS[s.speed] * run.overrunSeconds),
    y: round3(s.y),
    rot: round3(wrapAngle(run.pose.current)),
    mode: s.mode,
    grav: s.gravity,
    alive: s.alive,
    fx: sim.activeBoostBits(),
    att: sim.attempt,
    tm,
  };
}

export class NetSync {
  private nextStateAt = Number.NEGATIVE_INFINITY;
  private done = false;

  constructor(
    private readonly net: GameNetBridge,
    private readonly startAt: number,
  ) {}

  /** The race is over for the local player: nothing more is sent. */
  get finished(): boolean {
    return this.done;
  }

  /** The time of a race against the clock is up: nothing more is sent. */
  stop(): void {
    this.done = true;
  }

  /** Sends the race events of one frame (and the position right after a jump in the run). */
  onEvents(events: readonly RunEvent[], run: LocalRun, serverOffset: number): void {
    if (this.done || !run.isStarted) return;
    let sendState = false;
    for (const e of events) {
      const tm = wireTime(raceTimeAt(e.time, serverOffset, this.startAt));
      const raceEvent = raceEventFor(e, tm);
      if (raceEvent) this.net.sendEvent(raceEvent);
      const type = e.event.type;
      if (type === 'death' || type === 'respawn' || type === 'levelStart') sendState = true;
      if (type === 'raceFinished') {
        // One last position at the finish line, then silence.
        this.sendState(run, serverOffset);
        this.done = true;
        return;
      }
    }
    if (sendState) this.sendState(run, serverOffset);
  }

  /** Regular position updates for a frame at engine-clock time `now`. */
  update(now: number, run: LocalRun, serverOffset: number): void {
    if (this.done || !run.isStarted || now < this.nextStateAt) return;
    this.sendState(run, serverOffset);
    // Keep the average rate even when frames do not divide the interval evenly.
    this.nextStateAt =
      now - this.nextStateAt < STATE_INTERVAL_MS * 2
        ? this.nextStateAt + STATE_INTERVAL_MS
        : now + STATE_INTERVAL_MS;
  }

  private sendState(run: LocalRun, serverOffset: number): void {
    const raceTime = raceTimeAt(run.lastTickTime, serverOffset, this.startAt);
    // The server drops anything stamped before the start (it belongs to the countdown).
    if (raceTime < 0) return;
    this.net.sendState(netStateOf(run, wireTime(raceTime)));
  }
}
